import { Button } from '@grofers/react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { isArray } from 'lodash-es';
import { grey } from 'sushiweb/tokens/color';
import Spinner from 'sushiweb/Spinner/Spinner';
import { InfoLine } from 'sushiweb/Icons';
import { Star } from '../../../../../UiKit/Star/Star';
import { componentMap } from '../../../../../components/serverDrivenUIComponents/compMapping';
import { useUpdateNode } from '../apis';
import Multiselect from 'multiselect-react-dropdown';
import '../HelpSectionForms/HelpSectionFormCss.scss';
import { Tooltip } from '../../../../../UiKit';

const FieldComp = ({
  field,
  ruleList = [],
  dataToSend,
  updateDataToSend,
  isMultiSelect = false,
  setIsChangesSaved = () => {},
}) => {
  const multiselectRef = useRef();
  const saveOptions = useCallback(
    (key, value) => {
      let multiSelecteValues = [];
      if (isMultiSelect) {
        // TODO: write a func to check if same value is already present i.e to incorporate object check in !dataToSend[field?.id].includes(value)

        if (
          dataToSend[field?.id]?.length &&
          !dataToSend[field?.id].includes(value)
        ) {
          multiSelecteValues = [...dataToSend[field?.id], value];
        } else {
          multiSelecteValues = [value];
        }
        value = multiSelecteValues;
      }
      updateDataToSend(key, value);
    },
    [isMultiSelect, updateDataToSend, dataToSend, field?.id],
  );

  // applicable for multiselectable select input
  const unSaveOption = useCallback(
    (key, valueDeSelected) => {
      const selectedValues = dataToSend[field?.id];
      let updatedValues = [];
      for (let i = 0; i < selectedValues.length; i++) {
        const item = selectedValues[i];
        if (
          field?.type === 'combo_select_integer' &&
          item.select === valueDeSelected.select &&
          item.value === valueDeSelected.value
        )
          continue;
        if (item == valueDeSelected) continue;
        updatedValues = [...updatedValues, item];
      }
      updateDataToSend(key, updatedValues);
    },
    [dataToSend, field?.id, updateDataToSend],
  );

  let CompName = null;
  if (field.type in componentMap) CompName = componentMap[`${field.type}`];

  return (
    <div className='tw-flex tw-flex-col tw-h-max  tw-flex-1 tw-gap-2'>
      <div className='tw-flex tw-gap-2 tw-w-full'>
        {field.info?.rules?.length ? (
          <Tooltip
            position='bottom'
            border={false}
            title={<InfoLine size={14} color='#d02a38' />}
            widthMaxContent={false}
            labelWidth='50'
            label={() => {
              return (
                <div className='tw-flex tw-flex-col tw-flex-wrap tw-gap-2'>
                  {field.info?.rules.map((rule, index) => {
                    return (
                      <div
                        className='tw-flex tw-gap-2 tw-text-100 tw-font-regular'
                        key={`helpdesk_form_field_info_${index}`}
                      >
                        <span className='tw-text-300 tw-font-regular'>{`${
                          index + 1
                        }. `}</span>
                        <span className='tw-text-300 tw-font-regular'>
                          {rule}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        ) : null}

        <div className=' tw-color-[#1F1F1F] tw-flex tw-gap-1 tw-items-center'>
          <span className='tw-tex-300 tw-font-semibold'>{field?.label}</span>
          {field.info?.rule_type ? (
            <span className='tw-tex-200 tw-font-medium tw-ml-1'>{`(${field.info.rule_type})`}</span>
          ) : null}
          <span>{field?.required && <Star icon='*' />}</span>
        </div>
      </div>
      <div
        className='tw-flex tw-flex-col tw-w-full'
        id='helpdesk-edit-node-form'
        css={`
          .rdw-list-dropdown,
          .rdw-text-align-dropdown {
            z-index: 0 !important;
          }
        `}
      >
        {field?.isMultiSelect && field?.type === 'select' ? (
          <Multiselect
            showCheckbox
            ref={multiselectRef}
            avoidHighlightFirstOption
            singleSelect={false}
            loading={false}
            placeholder={`Search ${field?.label}`}
            options={field?.options}
            onSelect={(selectedList, selectedItem) => {
              const key = Object.keys(selectedItem)
                ? Object.keys(selectedItem)[0]
                : null;
              const value = key ? selectedItem[key] : null;
              saveOptions(field?.id, value);
            }}
            onRemove={(selectedList, removedItem) => {
              const key = Object.keys(removedItem)
                ? Object.keys(removedItem)[0]
                : null;
              const value = key ? removedItem[key] : null;
              unSaveOption(field?.id, value);
            }}
            displayValue='name'
            selectedValues={field?.value}
          />
        ) : (
          <CompName
            field={field}
            inputKey={field?.id}
            selectOptions={field?.options || field?.optionsOne}
            selectOptionsTwo={field?.optionsTwo}
            saveOptions={saveOptions}
            unSaveOptions={unSaveOption}
            dataToSend={dataToSend}
            isEditable={field?.isEditable}
            defaultValue={field?.value}
            isMultiSelect={
              field?.id === 'node_type' ? false : field?.isMultiSelect
            }
            setIsChangesSaved={setIsChangesSaved}
          />
        )}
      </div>
    </div>
  );
};

export const EditNodeForm = ({
  formFields,
  ruleList = [],
  refetchRootNodes = () => {},
  closeForm = () => {},
  nodeId = null,
  handleMenuActionPopUp = () => {},
  setRefetchParentEnable = () => {},
}) => {
  const [dataToSend, setDataToSend] = useState({});
  const [updatingNodeId, setUpdatingNode] = useState(null);
  const [changesSaved, setIsChangesSaved] = useState(true);

  const { mutate: updateNode, isLoading: nodeUpdating } = useUpdateNode(
    updatingNodeId,
    {
      onSuccess: (res) => {
        closeForm();
        if (dataToSend['node_type'] === 'root') refetchRootNodes();
      },
      onSettled: () => {
        setUpdatingNode(null);
      },
    },
    setRefetchParentEnable,
  );

  useEffect(() => {
    let data = {};
    const getIdList = (field) => {
      const valueList = field.value;
      if (!valueList?.length) return [];

      const isMultiSelect = field.isMultiSelect;
      if (!isMultiSelect) return field.value[0];

      let idList = [];
      for (let j = 0; j < valueList.length; j++) {
        if (valueList[j]?.id) idList.push(valueList[j]?.id);
        else idList.push(valueList[j]);
      }
      return idList;
    };

    for (let i = 0; i < formFields?.length; i++) {
      const field = formFields[i];

      if (ruleList.includes(field?.id)) {
        data = {
          ...data,
          [field.id]: isArray(field.value)
            ? getIdList(field)
            : field?.isMultiSelect
            ? [field.value]
            : field.value,
        };
      } else {
        data = {
          ...data,
          [field.id]: field?.isMultiSelect ? [field.value] : field.value,
        };
      }
    }
    setDataToSend(data);
  }, [formFields]);

  useEffect(() => {
    if (updatingNodeId) {
      const convertRuleIdsToMap = (dataToSend) => {
        let updatedDataToSend = {};
        let rule_map = {};

        Object.keys(dataToSend).forEach((key) => {
          if (ruleList.includes(key))
            rule_map = {
              ...rule_map,
              [key]: isArray(dataToSend[key])
                ? dataToSend[key]
                : [dataToSend[key]],
            };
          else {
            updatedDataToSend = {
              ...updatedDataToSend,
              [key]: dataToSend[key],
            };
          }
        });

        updatedDataToSend = {
          ...updatedDataToSend,
          rule_map: {
            ...rule_map,
          },
        };
        return updatedDataToSend;
      };

      const finalPayload = convertRuleIdsToMap(dataToSend);
      updateNode(finalPayload);
    }
  }, [updatingNodeId]);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      dataToSend.desk_name = 'customer';
      setUpdatingNode(nodeId);
    },
    [dataToSend, nodeId],
  );

  const updateDataToSend = useCallback(
    (key, value) => {
      const data = {
        ...dataToSend,
        [key]: value,
      };
      setDataToSend(data);
    },
    [dataToSend],
  );

  return (
    <form className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-rounded-2xl tw-pb-36 tw-mb-[20rem]'>
      <div className='tw-flex-col tw-gap-6 tw-px-8'>
        {formFields.map((field, index) => {
          if (field?.isVisible === undefined || field?.isVisible)
            return (
              <FieldComp
                field={field}
                ruleList={ruleList}
                dataToSend={dataToSend}
                updateDataToSend={updateDataToSend}
                setIsChangesSaved={setIsChangesSaved}
                isMultiSelect={field?.isMultiSelect}
                key={`${field.id}_${index}`}
              />
            );
        })}
      </div>
      <div
        className='tw-w-2/5 tw-bg-base-white tw-py-6 tw-px-5 tw-flex  tw-fixed tw-bottom-0 tw-z-[2000] tw-right-0 tw-gap-5 tw-justify-between'
        css={`
          box-shadow: 0px 0px 16px 0px rgba(28, 28, 28, 0.12);
        `}
      >
        <Button
          variant={'fill'}
          className='tw-h-14  tw-bg-indigo-400  hover:tw-bg-indigo-500 tw-px-3'
          onClick={(e) => {
            e.preventDefault();
            handleMenuActionPopUp(nodeId, 'duplicate');
          }}
        >
          Duplicate
        </Button>
        <div className='tw-flex tw-gap-5 '>
          <Button
            variant={'fill'}
            className='tw-h-14  tw-bg-red-500  hover:tw-bg-red-700 tw-w-28'
            onClick={(e) => {
              e.preventDefault();
              handleMenuActionPopUp(nodeId, 'delete');
            }}
          >
            Delete
          </Button>
          <Button
            variant={'fill'}
            className='tw-h-14 tw-w-28'
            onClick={onSubmit}
          >
            {nodeUpdating ? (
              <Spinner size='small' color={`${grey.z300}`} />
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};
